import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type StoreService from 'ember-data/store';
import type VisitComposite from 'garaje/models/visit-composite';
import type VisitorProfile from 'garaje/models/visitor-profile';
import type VisitStatsService from 'garaje/services/visit-stats';
import type { CursorPaginatedMeta, CursorPaginatedRecordArray } from 'garaje/types/pagination';
import type { VisitorProfileStats } from 'garaje/types/visitor-profile-stats';

import type VisitorsProfilesShowController from './controller';

export type { VisitorProfileStats } from 'garaje/types/visitor-profile-stats';

export interface VisitorsProfilesShowModel {
  visitorProfile: VisitorProfile;
  visitorProfileStats: VisitorProfileStats;
  upcomingVisits: VisitComposite[];
  previousVisits: VisitComposite[];
  upcomingVisitsMeta: CursorPaginatedMeta;
  previousVisitsMeta: CursorPaginatedMeta;
}

interface RouteParams {
  visitor_id: string;
}

interface QueryParams {
  upcomingVisitsCursor: string;
  previousVisitsCursor: string;
  // for navigating back to linked entry or invite
  from: string;
  id: string;
}

export const UPCOMING_VISITS_PAGE_SIZE = 5;
export const PREVIOUS_VISITS_PAGE_SIZE = 10;

export default class VisitorsProfilesShowRoute extends Route {
  @service declare store: StoreService;
  @service('visit-stats') declare visitStats: VisitStatsService;

  queryParams = {
    upcomingVisitsCursor: { refreshModel: true },
    previousVisitsCursor: { refreshModel: true },
  };

  async model({
    visitor_id: visitorId,
    upcomingVisitsCursor,
    previousVisitsCursor,
  }: RouteParams & QueryParams): Promise<VisitorsProfilesShowModel> {
    const visitorProfile = await this.store.findRecord('visitor-profile', visitorId, {
      reload: true,
    });
    const visitorProfileStats = await this.visitStats.fetch(visitorId);
    const upcomingVisitsPage = await this.#getVisitsPage(
      visitorId,
      'upcoming',
      upcomingVisitsCursor,
      UPCOMING_VISITS_PAGE_SIZE,
    );
    const previousVisitsPage = await this.#getVisitsPage(
      visitorId,
      'previous',
      previousVisitsCursor,
      PREVIOUS_VISITS_PAGE_SIZE,
    );

    return {
      visitorProfile,
      visitorProfileStats,
      upcomingVisits: upcomingVisitsPage.slice(),
      upcomingVisitsMeta: upcomingVisitsPage.meta ?? {},
      previousVisits: previousVisitsPage.slice(),
      previousVisitsMeta: previousVisitsPage.meta ?? {},
    };
  }

  setupController(
    controller: VisitorsProfilesShowController,
    model: VisitorsProfilesShowModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    // Reset loading states after model updates
    controller.upcomingVisitsIsLoading = false;
    controller.previousVisitsIsLoading = false;
  }

  resetController(controller: VisitorsProfilesShowController, isExiting: boolean, transition: Transition): void {
    super.resetController(controller, isExiting, transition);
    if (isExiting) {
      controller.closeRulesModal();
    }
  }

  async #getVisitsPage(
    visitorId: string,
    type: 'upcoming' | 'previous',
    cursor: string,
    limit: number,
  ): Promise<CursorPaginatedRecordArray<VisitComposite>> {
    return this.store.query('visit-composite', {
      page: {
        cursor: cursor || undefined,
        limit,
      },
      adapterOptions: {
        type,
        visitorProfileId: visitorId,
      },
    });
  }
}
